/**
 * Centralized Configuration Module for NarrativeAI Frontend
 * 
 * This module provides a single source of truth for all URL and environment configurations.
 * It supports auto-detection of deployment environments and provides fallback logic
 * for local development vs production deployment.
 * 
 * Usage:
 *   import { BACKEND_URL, WS_BACKEND_URL, SUPABASE_URL } from './config';
 */

/**
 * Get environment variable with fallback logic
 * @param {string} key - Primary environment variable key
 * @param {string} fallbackKey - Fallback environment variable key (optional)
 * @param {string} defaultValue - Default value if neither key nor fallback_key exist
 * @returns {string} Environment variable value or default
 */
const getEnv = (key, fallbackKey = null, defaultValue = null) => {
  return process.env[key] || 
         (fallbackKey ? process.env[fallbackKey] : null) || 
         defaultValue;
};

/**
 * Auto-detect current deployment environment
 * @returns {string} Environment type: 'railway', 'vercel', 'netlify', 'render', or 'local'
 */
const detectEnvironment = () => {
  const hostname = window.location.hostname;

  if (process.env.REACT_APP_VERCEL_URL || hostname.includes('vercel.app')) {
    return 'vercel';
  } else if (hostname.includes('railway.app')) {
    return 'railway';
  } else if (process.env.REACT_APP_NETLIFY_URL || hostname.includes('netlify.app')) {
    return 'netlify';
  } else if (process.env.REACT_APP_RENDER_URL || hostname.includes('render.com')) {
    return 'render';
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  } else {
    return 'unknown';
  }
};

/**
 * Auto-detect backend URL based on current environment
 * @returns {string} Backend URL appropriate for current environment
 */
const getAutoBackendUrl = () => {
  const environment = detectEnvironment();
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  switch (environment) {
    case 'vercel':
      // On Vercel, backend is a separate Railway service - must be set via env var
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    case 'railway':
      return `${protocol}//${hostname}`;
    case 'netlify':
      return `${protocol}//${hostname}`;
    case 'render':
      return `${protocol}//${hostname}`;
    case 'local':
      return 'http://localhost:8001';
    default:
      // For unknown environments, check if we're on HTTPS and use current origin
      if (protocol === 'https:') {
        return `${protocol}//${hostname}`;
      }
      // Fallback to localhost for HTTP
      return 'http://localhost:8001';
  }
};

/**
 * Auto-detect Supabase URL
 * @returns {string} Supabase URL
 */
const getAutoSupabaseUrl = () => {
  return process.env.REACT_APP_SUPABASE_URL || null;
};

// =============================================================================
// CORE CONFIGURATION VARIABLES
// =============================================================================

// Environment Detection
export const ENVIRONMENT = detectEnvironment();

// Debug logging for environment detection
if (typeof console !== 'undefined') {
  console.log('🔧 Frontend Config Debug:', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side',
    protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
    port: typeof window !== 'undefined' ? window.location.port : 'unknown',
    fullUrl: typeof window !== 'undefined' ? window.location.href : 'unknown',
    environment: ENVIRONMENT,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    isLocalhost: typeof window !== 'undefined' ? (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') : false
  });
}

// Backend Configuration with Auto-Detection + Manual Override Support
export const BACKEND_URL = (() => {
  // For local development, prioritize local backend URL
  if (ENVIRONMENT === 'local') {
    const localUrl = process.env.REACT_APP_LOCAL_BACKEND_URL;
    if (localUrl) {
      console.log('🔧 Using local backend URL:', localUrl);
      return localUrl;
    }
    // Fallback to localhost if no local URL specified
    console.log('🔧 Using default local backend URL: http://localhost:8001');
    return 'http://localhost:8001';
  }
  
  // For other environments, use the standard logic
  return getEnv(
    'REACT_APP_BACKEND_URL',
    null,
    getAutoBackendUrl()
  );
})();

// API Base URL (Backend URL + /api path)
export const API_BASE_URL = `${BACKEND_URL}/api`;

// WebSocket URL (automatically convert HTTP/HTTPS to WS/WSS)
export const WS_BACKEND_URL = BACKEND_URL
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

// Supabase Configuration (Production for all environments as requested)
export const SUPABASE_URL = getEnv(
  'REACT_APP_SUPABASE_URL',
  // Only use local fallback when actually in local environment  
  ENVIRONMENT === 'local' ? 'REACT_APP_LOCAL_SUPABASE_URL' : null,
  getAutoSupabaseUrl()
);

export const SUPABASE_ANON_KEY = getEnv(
  'REACT_APP_SUPABASE_ANON_KEY',
  ENVIRONMENT === 'local' ? 'REACT_APP_LOCAL_SUPABASE_ANON_KEY' : null,
  null
);

// Development Configuration Options
export const IS_DEVELOPMENT = ENVIRONMENT === 'local';
export const IS_PRODUCTION = ENVIRONMENT !== 'local';

// Debug logging for final configuration
if (typeof console !== 'undefined') {
  console.log('🎯 Frontend Config URLs:', {
    backendUrl: BACKEND_URL,
    apiBaseUrl: API_BASE_URL,
    wsBackendUrl: WS_BACKEND_URL,
    supabaseUrl: SUPABASE_URL,
    environment: ENVIRONMENT,
    isDevelopment: IS_DEVELOPMENT,
    envVars: {
      REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
      REACT_APP_LOCAL_BACKEND_URL: process.env.REACT_APP_LOCAL_BACKEND_URL
    }
  });
}

// WebSocket Configuration
export const WS_CONFIG = {
  // WebSocket connection URL for room connections - consistent across environments
  getRoomWsUrl: (roomId, token) => {
    // Use consistent path - platform handles routing differences
    return `${WS_BACKEND_URL}/api/ws-v2/${roomId}?token=${token}`;
  },
  
  // Legacy WebSocket URL (if needed for compatibility)
  getLegacyWsUrl: (roomId, token) => `${WS_BACKEND_URL}/api/ws/${roomId}?token=${token}`,
  
  // WebSocket connection options
  options: {
    // Reconnection settings
    reconnectDelay: 1000,
    maxReconnectAttempts: 5,
    
    // Ping/pong settings for connection health
    pingInterval: 30000,
    pongTimeout: 5000
  }
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get configuration summary for debugging
 * @returns {object} Configuration summary object
 */
export const getConfigSummary = () => {
  return {
    environment: ENVIRONMENT,
    backendUrl: BACKEND_URL,
    apiBaseUrl: API_BASE_URL,
    wsBackendUrl: WS_BACKEND_URL,
    supabaseUrl: SUPABASE_URL,
    isDevelopment: IS_DEVELOPMENT,
    isProduction: IS_PRODUCTION
  };
};

/**
 * Print configuration summary to console (for debugging)
 */
export const printConfigSummary = () => {
  console.log('🔧 NarrativeAI Frontend Configuration');
  console.log('=' * 40);
  console.log(`Environment: ${ENVIRONMENT}`);
  console.log(`Backend URL: ${BACKEND_URL}`);
  console.log(`API Base URL: ${API_BASE_URL}`);
  console.log(`WebSocket URL: ${WS_BACKEND_URL}`);
  console.log(`Supabase URL: ${SUPABASE_URL}`);
  console.log(`Development Mode: ${IS_DEVELOPMENT}`);
  console.log('=' * 40);
};

// Auto-print config summary in development mode (can be disabled via env var)
if (IS_DEVELOPMENT && getEnv('REACT_APP_DEBUG_CONFIG', null, 'false') === 'true') {
  printConfigSummary();
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

const config = {
  ENVIRONMENT,
  BACKEND_URL,
  API_BASE_URL,
  WS_BACKEND_URL,
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  IS_DEVELOPMENT,
  IS_PRODUCTION,
  WS_CONFIG,
  getConfigSummary,
  printConfigSummary
};

export default config;
